import { BrowserAuthError } from "@azure/msal-browser"
import * as Sentry from "@sentry/react"
import type { ApiError } from "./api/errors/ApiError"
import { getPublicConfig } from "./config/config"

const { sentryDsn, environment } = getPublicConfig()

function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof Error &&
    typeof (error as ApiError).status === "number" &&
    typeof (error as ApiError).url === "string" &&
    typeof (error as ApiError).method === "string"
  )
}

Sentry.init({
  dsn: sentryDsn,
  release: import.meta.env.VITE_BUILD_VERSION,
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  // Disable Sentry in local environments
  enabled: environment !== "local",
  environment,
  beforeSend(event, hint) {
    const error = hint.originalException

    // Filter out user_cancelled - expected when user closes auth popup
    if (error instanceof BrowserAuthError && error.errorCode === "user_cancelled") {
      return null
    }

    if (isApiError(error)) {
      const isAuthFailure = error.status === 403
      event.tags = {
        ...event.tags,
        error_type: isAuthFailure ? "authorization_failure" : "api_error",
        http_status: error.status,
        http_method: error.method
      }
      event.extra = {
        ...event.extra,
        url: error.url,
        response_body: error.body
      }
    }

    return event
  }
})

/** Set Sentry user context with user ID */
export function setSentryUser(id: string | null) {
  Sentry.setUser(id ? { id } : null)
}
